import { Point2D, MapGeoJSONFeature, LngLat } from "maplibre-gl";

import SourceWithLayers from "./SourceWithLayers";
import AbstractUserEventsHandler from "./AbstractUserEventsHandler";
import MapProvider from "./MapProvider";

const toPaddedBounds = (point: Point2D): [[number, number], [number, number]] => {
    return [
        // sw:
        [point.x - 5, point.y + 5],
        // ne:
        [point.x + 5, point.y - 5]
    ];
};

export interface HoverHandler {
    /**
     * Synthetic hover event. Happens only when an interactive feature is hovered from any previous state, or we un-hover completely.
     * @param lngLat The hovering coordinates. Will likely be nearby the feature but might not literally be on it (taking padding into account).
     * @param feature The hovered z-top-most feature, first one as queried from the map.
     * @param sourceWithLayers The source with layers to which the feature belongs to.
     * @param longHover Whether this is an immediate (false) or long (true) hover. Long hovers only are fired if the mouse stays long enough on the same feature without clicking on it.
     */
    (lngLat: LngLat, feature: MapGeoJSONFeature, sourceWithLayers: SourceWithLayers, longHover: boolean): void;
}

export enum ClickType {
    LEFT_CLICK,
    DELAYED_LEFT_CLICK,
    RIGHT_CLICK
}

export interface ClickHandler {
    /**
     * Click event. Happens when the user clicks on a feature belonging to any of the registered sources/layers.
     * @param lngLat The click coordinates. Will likely be nearby the feature but might not literally be on it (taking padding into account).
     * @param feature The clicked z-top-most feature, first one as queried from the map.
     * @param sourceWithLayers The source with layers to which the feature belongs to.
     * @param clickType The type of click issued by the user (left click vs delayed left click vs right click / long tap)
     */
    (lngLat: LngLat, feature: MapGeoJSONFeature, sourceWithLayers: SourceWithLayers, clickType: ClickType): void;
}

/**
 * This is the place where we handle the user events on the map (mousemove/hover and click mostly).
 * To have full control on hovers and clicks when multiple overlapping layers are present, that logic must be centralized here.
 */
export default class HoverClickEventsHandler extends AbstractUserEventsHandler {
    private enabled = true;
    private hoveringLngLat: LngLat;
    private hoveringPoint: Point2D;
    private hoveringFeature: MapGeoJSONFeature;
    private hoveringSourceWithLayers: SourceWithLayers;
    private onHoverCallback: HoverHandler;
    // delayed hover control:
    // The first hover we do after the map moves is longer:
    private firstHoverAfterMapMoveDelayMS = 800;
    // Followup hovers with the same non-moving map are quicker ("hovering around mode"):
    private followupHoverWithStaticMapDelayMS = 300;
    private longHoverTimeoutHandlerID: number;
    // Control flag to indicate that the coming hover is the first one since the map is "quiet" again:
    private firstDelayedHoverSinceMapMove = true;

    private lastClickedLngLat: LngLat;
    private lastClickedFeature: MapGeoJSONFeature;
    private lastClickedSourceWithLayers: SourceWithLayers;
    private onClickCallback: ClickHandler;

    private delayedClickMS = 500;
    private delayedClickTimeoutHandlerID: number;

    private lastCursorStyle = "grab";

    /**
     * Enables listening to user events for the given map, with the given callbacks.
     * @param onHoverCallback Synthetic hover events callback.
     * @param onClickCallback Click events callback.
     */
    public listenToUserEvents = (onHoverCallback?: HoverHandler, onClickCallback?: ClickHandler) => {
        const map = MapProvider.map;
        this.onHoverCallback = onHoverCallback;
        this.onClickCallback = onClickCallback;
        map.on("mousemove", this.onMouseMove);
        this.listenToMapClicks(ClickType.LEFT_CLICK);
        this.listenToMapClicks(ClickType.RIGHT_CLICK);
        map.on("movestart", () => {
            this.firstDelayedHoverSinceMapMove = true;
            window.clearTimeout(this.longHoverTimeoutHandlerID);
        });
        map.on("mouseout", () => {
            // Preventing accidental de-hover event if we actually leave the map canvas.
            // Since this could potentially be about jumping into a map popup, so we leave that up to the caller.
            window.clearTimeout(this.longHoverTimeoutHandlerID);
        });
        map.on("mouseover", this.onMouseMove);
        map.on("mousedown", () => {
            window.clearTimeout(this.delayedClickTimeoutHandlerID);
            this.lastCursorStyle = map.getCanvas().style.cursor;
            map.getCanvas().style.cursor = "grabbing";
        });
        map.on("mouseup", () => {
            map.getCanvas().style.cursor = this.lastCursorStyle;
        });
        map.on("dblclick", () => window.clearTimeout(this.delayedClickTimeoutHandlerID));
    };

    public enable = (enabled: boolean) => {
        this.enabled = enabled;
        if (!enabled) {
            window.clearTimeout(this.longHoverTimeoutHandlerID);
        }
    };

    private onMouseMove = (ev) => {
        const map = MapProvider.map;
        if (!this.enabled || map.isMoving()) {
            // We ensure no unwanted hover handling while the map moves
            return;
        }
        const hoveredTopFeature = map.queryRenderedFeatures(toPaddedBounds(ev.point), {
            layers: this.interactiveLayerIDs
        })?.[0];

        // flag to determine whether a change happened, such as no-hover -> hover or vice-versa:
        let hoverChangeDetected = false;
        // flag to detect whether the mouse is moving along the hovered feature (not stopped on it):
        let mouseInMotionOverHoveredFeature = false;
        if (hoveredTopFeature) {
            // Workaround/hack to avoid listening to map style POIs without ID (bad data):
            if (hoveredTopFeature.source === "poiTiles" && !hoveredTopFeature.properties?.id) {
                return;
            }

            if (!this.hoveringFeature) {
                // no hover -> hover
                map.getCanvas().style.cursor = "pointer";
                hoverChangeDetected = true;
            } else if (hoveredTopFeature.id !== this.hoveringFeature.id) {
                // hovering from one feature to another one (from the same or different layer/source):
                hoverChangeDetected = true;
            } else {
                // (else we're hovering along the same feature)
                if (
                    Math.abs(ev.point.x - this.hoveringPoint?.x) > 0 ||
                    Math.abs(ev.point.y - this.hoveringPoint?.y) > 0
                ) {
                    mouseInMotionOverHoveredFeature = true;
                }
            }
        } else {
            if (this.hoveringFeature) {
                // hover -> no hover (un-hover)
                map.getCanvas().style.cursor = "grab";
                hoverChangeDetected = true;
            }
        }

        this.hoveringLngLat = ev.lngLat;
        this.hoveringPoint = ev.point;
        this.hoveringFeature = hoveredTopFeature;
        this.hoveringSourceWithLayers = hoveredTopFeature
            ? this.interactiveSourcesAndLayers[hoveredTopFeature.source]
            : undefined;

        if (hoverChangeDetected) {
            if (this.onHoverCallback) {
                // (If de-hovering this should fire undefined, undefined):
                this.onHoverCallback(ev.lngLat, this.hoveringFeature, this.hoveringSourceWithLayers, false);
            }
        }

        if (hoverChangeDetected || mouseInMotionOverHoveredFeature) {
            this.restartLongHoverTimeout();
        }
    };

    private restartLongHoverTimeout = () => {
        window.clearTimeout(this.longHoverTimeoutHandlerID);
        this.longHoverTimeoutHandlerID = window.setTimeout(
            this.handleLongHoverTimeout,
            this.firstDelayedHoverSinceMapMove
                ? this.firstHoverAfterMapMoveDelayMS
                : this.followupHoverWithStaticMapDelayMS
        );
    };

    private handleLongHoverTimeout = () => {
        // We try to avoid firing long hovers when the feature was just clicked
        // (requires a safe-ish efficient way to compare features coming from different events, like IDs):
        if (
            !this.hoveringFeature ||
            !this.lastClickedFeature ||
            // properties IDs, if any, are expected to be most reliable for comparison:
            this.lastClickedFeature.properties.id !== this.hoveringFeature.properties.id ||
            (!this.lastClickedFeature.properties.id &&
                !this.hoveringFeature.properties.id &&
                // otherwise (auto-generated) IDs are used next, but they could potentially be different for essentially the same core item:
                this.lastClickedFeature.id !== this.hoveringFeature.id)
        ) {
            this.firstDelayedHoverSinceMapMove = false;
            if (this.onHoverCallback) {
                this.onHoverCallback(this.hoveringLngLat, this.hoveringFeature, this.hoveringSourceWithLayers, true);
            }
        }
    };

    private listenToMapClicks = (clickType: ClickType) => {
        const map = MapProvider.map;
        map.on(clickType === ClickType.LEFT_CLICK ? "click" : "contextmenu", (ev) => {
            if (!this.enabled || map.isMoving()) {
                // We avoid any accidental click handling while the map moves
                return;
            }
            this.lastClickedLngLat = ev.lngLat;
            this.lastClickedFeature = map.queryRenderedFeatures(toPaddedBounds(ev.point), {
                layers: this.interactiveLayerIDs
            })?.[0];
            this.lastClickedSourceWithLayers = this.lastClickedFeature
                ? this.interactiveSourcesAndLayers[this.lastClickedFeature.source]
                : null;
            if (this.onClickCallback) {
                this.onClickCallback(ev.lngLat, this.lastClickedFeature, this.lastClickedSourceWithLayers, clickType);
                if (clickType === ClickType.LEFT_CLICK) {
                    this.restartLongClickTimeout();
                }
            }
        });
    };

    private restartLongClickTimeout = () => {
        window.clearTimeout(this.delayedClickTimeoutHandlerID);
        this.delayedClickTimeoutHandlerID = window.setTimeout(this.handleDelayedClickTimeout, this.delayedClickMS);
    };

    private handleDelayedClickTimeout = () => {
        this.onClickCallback(
            this.lastClickedLngLat,
            this.lastClickedFeature,
            this.lastClickedSourceWithLayers,
            ClickType.DELAYED_LEFT_CLICK
        );
    };
}
